import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, Loader2, MessageCircleQuestion, Users, NotebookText, BookMarked, BookOpen } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { ModeChips, GroupHeading, QuestionGrid, PeopleList, NotesList, CommentaryList, WordCard, BibleList } from "@/components/search/SearchBits";
import { PassageResult } from "@/components/search/PassageResult";
import { SuggestStrip } from "@/components/search/SuggestStrip";

const EXAMPLES = ["John 3:16", "Nicodemus", "Where are you?", "faith", "G26"];

export default function SearchPage() {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const q = sp.get("q") || "";
  const mode = sp.get("mode") || "all";
  const [term, setTerm] = useState(q);
  useEffect(() => setTerm(q), [q]);
  const limit = mode === "all" ? 6 : 50;

  const { data, isFetching } = useQuery({
    queryKey: ["search", q, limit],
    queryFn: async () => (await api.get("/search", { params: { q, limit } })).data,
    enabled: q.trim().length >= 2,
    placeholderData: (prev) => prev,
  });
  const setMode = (m) => setSp((p) => { const n = new URLSearchParams(p); m === "all" ? n.delete("mode") : n.set("mode", m); return n; });
  const submit = (e) => { e.preventDefault(); if (term.trim()) setSp({ q: term.trim() }); };

  const counts = data?.counts || {};
  const groups = data?.groups || {};
  const terms = data?.terms || [];
  const total = Object.entries(counts).reduce((a, [k, b]) => a + (k === "notes_application" ? 0 : b || 0), 0);
  const show = (key) => mode === "all" || mode === key;
  const more = (key) => () => setMode(key);

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8" data-testid="search-page">
      <Seo title={q ? `Search: ${q}` : "Search"} description="Look up a passage, a question, a person, or a word across QuestionGod's recorded questions and classic commentaries." />
      <header className="mb-8">
        <span className="font-mono text-xs uppercase tracking-[0.25em] text-gold">Search</span>
        <h1 className="mt-2 font-serif text-4xl font-bold tracking-tight text-foreground sm:text-5xl">Find it in the Word.</h1>
        <form onSubmit={submit} className="relative mt-6 max-w-2xl">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="A verse (John 3:16), a question, a person, a word, or a Strong's number…" data-testid="search-page-input" autoFocus
            className="w-full rounded-full border border-border bg-card py-3.5 pl-12 pr-28 text-base text-foreground outline-none placeholder:text-muted-foreground focus:border-primary" />
          <button type="submit" data-testid="search-page-submit" className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">Search</button>
        </form>
        <SuggestStrip term={term} committed={q} onPick={(v) => setSp({ q: v })} onNavigate={(href) => navigate(href, { state: { from: `/search?q=${encodeURIComponent(term.trim())}` } })} />
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground" data-testid="search-scope">
          Searches {data?.scope?.questions?.toLocaleString() || "every"} recorded question{data?.scope?.questions === 1 ? "" : "s"} in Scripture — the question text, its verse, who asked and was asked, our study notes — plus every verse of the Bible (KJV) and the five classic commentaries. Type a reference to look up a passage directly.
        </p>
        {!q && (
          <div className="mt-4 flex flex-wrap gap-2">
            {EXAMPLES.map((ex) => <button key={ex} type="button" onClick={() => setSp({ q: ex })} data-testid={`search-example-${ex.replace(/\W+/g, "-")}`} className="rounded-full border border-border px-3 py-1 text-sm text-foreground hover:border-gold">{ex}</button>)}
          </div>
        )}
      </header>

      {q.trim().length >= 2 && data && (
        <div className="space-y-10">
          {data.intent === "word" && <WordCard word={data.word} />}
          {data.intent === "passage" && (
            <>
              <ModeChips mode="all" setMode={() => {}} counts={counts} intent="passage" />
              <PassageResult data={data} />
            </>
          )}
          {data.suggestions?.length > 0 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="search-suggestions">
              <span className="text-sm text-muted-foreground">{total === 0 ? "Did you mean" : "Looking for"}</span>
              {data.suggestions.map((sg) => (
                <button key={sg.q} type="button" onClick={() => setSp({ q: sg.q })} data-testid={`search-suggestion-${sg.q.replace(/\W+/g, "-")}`}
                  className="rounded-full border border-gold/60 bg-gold/10 px-3 py-1 text-sm font-medium text-foreground transition-colors hover:bg-gold/20">
                  {sg.label}<span className="ml-1.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{sg.kind}</span>
                </button>
              ))}
              <span className="text-sm text-muted-foreground">?</span>
            </div>
          )}
          {data.intent === "text" && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <ModeChips mode={mode} setMode={setMode} counts={counts} intent="text" />
                <p className="font-mono text-xs text-muted-foreground" data-testid="search-total">{isFetching ? <Loader2 className="inline h-3.5 w-3.5 animate-spin" /> : `${total.toLocaleString()} result${total === 1 ? "" : "s"} for “${q}”`}</p>
              </div>
              {total === 0 && !isFetching && (
                <section className="rounded-2xl border border-dashed border-border p-8 text-center" data-testid="search-empty">
                  <p className="font-serif text-2xl text-foreground">Nothing matched “{q}”.</p>
                  <p className="mt-2 text-sm text-muted-foreground">{data.suggestions?.length ? "Pick a suggestion above, or try" : "Try"} a shorter word, a person's name, or a reference like “Genesis 3:9”. Quotes search an exact phrase.</p>
                </section>
              )}
              {show("questions") && groups.questions?.length > 0 && (
                <section data-testid="search-group-questions"><GroupHeading icon={MessageCircleQuestion} title="Questions" count={counts.questions} onMore={mode === "all" && counts.questions > groups.questions.length ? more("questions") : null} testId="search-more-questions" /><QuestionGrid items={groups.questions} terms={terms} /></section>
              )}
              {show("people") && groups.people?.length > 0 && (
                <section data-testid="search-group-people"><GroupHeading icon={Users} title="People" count={counts.people} onMore={mode === "all" && counts.people > groups.people.length ? more("people") : null} testId="search-more-people" /><PeopleList items={groups.people} /></section>
              )}
              {show("bible") && groups.bible?.length > 0 && (
                <section data-testid="search-group-bible"><GroupHeading icon={BookOpen} title="In the Bible" count={counts.bible} onMore={mode === "all" && counts.bible > groups.bible.length ? more("bible") : null} testId="search-more-bible" /><BibleList items={groups.bible} terms={terms} /></section>
              )}
              {show("notes") && (groups.notes?.length > 0 || groups.notes_application?.length > 0) && (
                <section data-testid="search-group-notes"><GroupHeading icon={NotebookText} title="Study notes" count={counts.notes} onMore={mode === "all" && counts.notes > groups.notes.length ? more("notes") : null} testId="search-more-notes" /><NotesList items={groups.notes} life={groups.notes_application} terms={terms} applicationTotal={counts.notes_application} /></section>
              )}
              {show("commentaries") && groups.commentaries?.length > 0 && (
                <section data-testid="search-group-commentaries"><GroupHeading icon={BookMarked} title="Commentaries" count={counts.commentaries} onMore={mode === "all" && counts.commentaries > groups.commentaries.length ? more("commentaries") : null} testId="search-more-commentaries" /><CommentaryList items={groups.commentaries} terms={terms} /></section>
              )}
              {mode !== "all" && !(groups[mode]?.length > 0) && !isFetching && total > 0 && (
                <p className="text-sm text-muted-foreground" data-testid="search-mode-empty">No {mode === "notes" ? "study notes" : mode === "bible" ? "Bible verses" : mode} match “{q}”. <button type="button" className="text-primary hover:underline" onClick={() => setMode("all")}>Show all results</button></p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
